import type { FormatPreset } from '@cinematic-engine/core';
import type { CompiledProject } from '@cinematic-engine/video-dsl';
import { create } from 'zustand';
import {
  activeClipAt,
  clampClip,
  createId,
  makeDefaultTracks,
  snapTime,
  timelineDuration,
} from '../media/editor-utils';
import { createAssetFromFile } from '../media/media-utils';
import { assetOpfsKey, persistAssetBlob, removeAssetBlob } from '../media/opfs-store';
import { releaseMedia } from '../media/render-engine';
import type {
  EditSnapshot,
  ExportState,
  StudioAsset,
  StudioClip,
  StudioTrack,
  TrackKind,
} from '../media/types';

export type ViewId = 'project' | 'assets' | 'storyboard' | 'canvas' | 'render' | 'script';

export interface StudioState {
  view: ViewId;
  project: CompiledProject | null;
  currentFrame: number;
  selectedNodeId: string | null;
  selectedFormat: FormatPreset | null;
  safeAreasVisible: boolean;
  canvasZoom: number;
  diagnosticsOpen: boolean;
  scriptText: string;

  assets: StudioAsset[];
  tracks: StudioTrack[];
  isPlaying: boolean;
  loop: boolean;
  exportState: ExportState;
  canUndo: boolean;
  canRedo: boolean;

  setView: (view: ViewId) => void;
  setProject: (project: CompiledProject | null) => void;
  seekFrame: (frame: number) => void;
  selectNode: (id: string | null) => void;
  setFormat: (preset: FormatPreset | null) => void;
  toggleSafeAreas: () => void;
  setCanvasZoom: (zoom: number) => void;
  toggleDiagnostics: () => void;
  setScriptText: (text: string) => void;

  importFiles: (files: FileList | File[]) => Promise<number>;
  removeAsset: (id: string) => void;
  loadMediaSession: (assets: StudioAsset[], tracks: StudioTrack[]) => void;
  setAssetUrl: (id: string, url: string) => void;
  addTrack: (kind: TrackKind) => void;
  removeTrack: (id: string) => void;
  commitEdit: () => void;
  addClip: (assetId: string, trackId: string, start: number) => string | null;
  removeClip: (id: string) => void;
  updateClip: (
    id: string,
    patch: Partial<Pick<StudioClip, 'start' | 'duration' | 'trimStart' | 'trimEnd'>>
  ) => void;
  togglePlay: () => void;
  setLoop: (loop: boolean) => void;
  setExportState: (patch: Partial<ExportState>) => void;
  cancelExport: () => void;
  undo: () => void;
  redo: () => void;
  hydrate: (state: EditSnapshot & { loop: boolean; scriptText?: string }) => void;
  __resetEditHistory: () => void;
}

const AUTOSAVE_KEY = 'cinematic-engine:edit-session:v1';
const UNDO_LIMIT = 50;

function cloneEdit(state: StudioState): EditSnapshot {
  return structuredClone({ assets: state.assets, tracks: state.tracks });
}

export function makeEmptyExportState(): ExportState {
  return { status: 'idle', progress: 0, message: '', blobUrl: null };
}

export const useStudio = create<StudioState>((set, get) => {
  const undoStack: EditSnapshot[] = [];
  const redoStack: EditSnapshot[] = [];
  const pushUndo = () => {
    undoStack.push(cloneEdit(get()));
    if (undoStack.length > UNDO_LIMIT) undoStack.shift();
    redoStack.length = 0;
    set({ canUndo: undoStack.length > 0, canRedo: false });
  };

  const applyEdit = (snapshot: EditSnapshot) => {
    set({ assets: snapshot.assets, tracks: snapshot.tracks });
    const frame = get().currentFrame;
    const durationFrames = snapshotToDurationFrames(snapshot, get());
    if (frame > durationFrames) get().seekFrame(durationFrames);
  };

  return {
    view: 'canvas',
    project: null,
    currentFrame: 0,
    selectedNodeId: null,
    selectedFormat: null,
    safeAreasVisible: false,
    canvasZoom: 1,
    diagnosticsOpen: false,
    scriptText: '',

    assets: [],
    tracks: makeDefaultTracks(),
    isPlaying: false,
    loop: false,
    exportState: makeEmptyExportState(),
    canUndo: false,
    canRedo: false,

    setView: (view) => set({ view }),
    setProject: (project) => set({ project, currentFrame: 0, selectedNodeId: null }),
    seekFrame: (frame) =>
      set((state) => {
        const max = projectDurationFrames(state);
        const clamped = Math.min(Math.max(0, Math.round(frame)), max);
        return { currentFrame: clamped };
      }),
    selectNode: (id) => set({ selectedNodeId: id }),
    setFormat: (preset) => set({ selectedFormat: preset }),
    toggleSafeAreas: () => set((s) => ({ safeAreasVisible: !s.safeAreasVisible })),
    setCanvasZoom: (zoom) => set({ canvasZoom: zoom }),
    toggleDiagnostics: () => set((s) => ({ diagnosticsOpen: !s.diagnosticsOpen })),
    setScriptText: (text) => set({ scriptText: text }),

    importFiles: async (files) => {
      const list = Array.from(files);
      if (list.length === 0) return 0;
      pushUndo();
      let imported = 0;
      for (const file of list) {
        try {
          const asset = await createAssetFromFile(file);
          const persistInOpfs =
            asset.size > 4 * 1024 * 1024 || asset.kind === 'glb' || asset.kind === 'gltf';
          if (persistInOpfs && (await persistAssetBlob(asset.id, file))) {
            asset.opfsKey = assetOpfsKey(asset.id);
          }
          set((s) => ({ assets: [...s.assets, asset] }));
          imported++;
        } catch (error) {
          console.error(`Failed to import ${file.name}`, error);
        }
      }
      return imported;
    },

    removeAsset: (id) => {
      const state = get();
      const asset = state.assets.find((a) => a.id === id);
      pushUndo();
      set((s) => ({
        assets: s.assets.filter((a) => a.id !== id),
        tracks: s.tracks.map((track) => ({
          ...track,
          clips: track.clips.filter((c) => c.assetId !== id),
        })),
      }));
      if (asset?.url) URL.revokeObjectURL(asset.url);
      if (asset?.opfsKey) void removeAssetBlob(asset.id);
      releaseMedia(id);
    },

    setAssetUrl: (id, url) => {
      set((s) => ({
        assets: s.assets.map((a) => (a.id === id ? { ...a, url } : a)),
      }));
    },

    loadMediaSession: (assets, tracks) => {
      const previous = get().assets;
      pushUndo();
      set({ assets, tracks, currentFrame: 0, selectedNodeId: null });
      for (const asset of previous) {
        if (asset.url) URL.revokeObjectURL(asset.url);
        releaseMedia(asset.id);
      }
    },

    addTrack: (kind) => {
      pushUndo();
      const name = `${kind === 'video' ? 'Video' : kind === 'audio' ? 'Audio' : 'Text'} ${get().tracks.filter((t) => t.kind === kind).length + 1}`;
      const track: StudioTrack = { id: createId('track'), kind, name, clips: [] };
      set((s) => ({ tracks: [...s.tracks, track] }));
    },

    removeTrack: (id) => {
      pushUndo();
      set((s) => ({ tracks: s.tracks.filter((t) => t.id !== id) }));
    },

    commitEdit: () => {
      pushUndo();
    },

    addClip: (assetId, trackId, start) => {
      const state = get();
      const track = state.tracks.find((t) => t.id === trackId);
      const asset = state.assets.find((a) => a.id === assetId);
      if (!track || !asset) return null;
      const fps = projectFps(state);
      const snapped = snapTime(start, fps);
      const clip: StudioClip = {
        id: createId('clip'),
        trackId,
        assetId,
        start: snapped,
        duration: Math.max(0.5, asset.duration ?? 5),
        trimStart: 0,
        trimEnd: asset.duration ?? 0,
      };
      pushUndo();
      set((s) => ({
        tracks: s.tracks.map((t) =>
          t.id === trackId
            ? { ...t, clips: [...t.clips, clampClip(clip, { ...t, clips: t.clips })] }
            : t
        ),
      }));
      set({ selectedNodeId: clip.id });
      return clip.id;
    },

    removeClip: (id) => {
      pushUndo();
      set((s) => ({
        tracks: s.tracks.map((t) => ({ ...t, clips: t.clips.filter((c) => c.id !== id) })),
        selectedNodeId: s.selectedNodeId === id ? null : s.selectedNodeId,
      }));
    },

    updateClip: (id, patch) => {
      set((s) => ({
        tracks: s.tracks.map((t) => ({
          ...t,
          clips: t.clips.map((c) => {
            if (c.id !== id) return c;
            const next = { ...c, ...patch };
            const clamped = clampClip(next, { ...t, clips: t.clips });
            return {
              ...clamped,
              trimStart: patch.trimStart ?? c.trimStart,
              trimEnd: patch.trimEnd ?? c.trimEnd,
            };
          }),
        })),
      }));
    },

    togglePlay: () => {
      const state = get();
      if (state.isPlaying) {
        set({ isPlaying: false });
        return;
      }
      const max = projectDurationFrames(state);
      set({ isPlaying: true, currentFrame: state.currentFrame >= max ? 0 : state.currentFrame });
    },

    setLoop: (loop) => set({ loop }),

    setExportState: (patch) => set((s) => ({ exportState: { ...s.exportState, ...patch } })),

    cancelExport: () => set((s) => ({ exportState: { ...s.exportState, status: 'cancelled' } })),

    undo: () => {
      const previous = undoStack.pop();
      if (!previous) return;
      redoStack.push(cloneEdit(get()));
      applyEdit(previous);
      set({ canUndo: undoStack.length > 0, canRedo: redoStack.length > 0 });
    },

    redo: () => {
      const next = redoStack.pop();
      if (!next) return;
      undoStack.push(cloneEdit(get()));
      applyEdit(next);
      set({ canUndo: undoStack.length > 0, canRedo: redoStack.length > 0 });
    },

    hydrate: (snapshot) => {
      set({
        assets: snapshot.assets,
        tracks: snapshot.tracks,
        loop: snapshot.loop,
        currentFrame: 0,
        scriptText: snapshot.scriptText ?? '',
      });
    },

    __resetEditHistory: () => {
      undoStack.length = 0;
      redoStack.length = 0;
      set({ canUndo: false, canRedo: false });
    },
  };
});

function projectFps(state: StudioState): number {
  return state.project?.format.fps ?? state.selectedFormat?.fps ?? 24;
}

function projectDurationFrames(state: StudioState): number {
  const fps = projectFps(state);
  const editSeconds = timelineDuration(state.tracks);
  const dslFrames = state.project?.durationFrames ?? 0;
  return Math.max(dslFrames, Math.round(editSeconds * fps));
}

function snapshotToDurationFrames(snapshot: EditSnapshot, state: StudioState): number {
  const fps = state.project?.format.fps ?? state.selectedFormat?.fps ?? 24;
  return Math.max(
    state.project?.durationFrames ?? 0,
    Math.round(timelineDuration(snapshot.tracks) * fps)
  );
}

export function selectClipAtTime(state: StudioState, time: number): StudioClip | null {
  const seconds = time / projectFps(state);
  return (
    activeClipAt(state.tracks, 'video', seconds) ?? activeClipAt(state.tracks, 'audio', seconds)
  );
}

export function timelineEndSeconds(state: StudioState): number {
  return Math.max(state.project?.durationSeconds ?? 0, timelineDuration(state.tracks));
}

export function totalClipCount(state: StudioState): number {
  return state.tracks.reduce((acc, t) => acc + t.clips.length, 0);
}

export function exportFileName(projectName: string, format: string): string {
  const slug = projectName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${slug || 'cinematic'}-export.${format}`;
}

export { AUTOSAVE_KEY };
